import { useEffect, useRef } from 'react';
import { trackFrontendEvent } from '../../core/events';
export const PLAY_METRICS_EVENT = 'play_metrics' as const;
const PLAY_METRICS_INTERVAL_MS = 30000;
export const PLAY_SLOW_INPUT_THRESHOLD_MS = 100;
type PrimitiveValue = string | number | boolean | null | undefined;
type TrackEvent = (name: string, attributes?: Record<string, PrimitiveValue>, domain?: string) => void;
type PlaybackQualitySnapshot = {
  droppedVideoFrames?: number;
  totalVideoFrames?: number;
};
type PlayMetricsSupport = {
  frameStats?: boolean;
  longTask?: boolean;
  slowInput?: boolean;
};
type PlayMetricsControllerOptions = {
  now?: () => number;
  trackEvent?: TrackEvent;
};
type MetricsWindow = {
  droppedFrames: number;
  hasFrameStats: boolean;
  longTaskBlockedMs: number;
  longTaskCount: number;
  slowInputCount: number;
  slowInputMaxDelayMs: number;
  slowInputTotalDelayMs: number;
  totalFrames: number;
};
const createEmptyWindow = (): MetricsWindow => ({
  droppedFrames: 0,
  hasFrameStats: false,
  longTaskBlockedMs: 0,
  longTaskCount: 0,
  slowInputCount: 0,
  slowInputMaxDelayMs: 0,
  slowInputTotalDelayMs: 0,
  totalFrames: 0
});
const toNonNegativeInt = (value: number | undefined): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value) || Number.isNaN(value)) {
    return undefined;
  }
  return Math.round(Math.max(0, value));
};
const readMemoryUsedBytes = (): number | undefined => {
  try {
    const memory = (performance as unknown as {
      memory?: {
        usedJSHeapSize?: number;
      };
    }).memory;
    return typeof memory?.usedJSHeapSize === 'number' ? memory.usedJSHeapSize : undefined;
  } catch {
    return undefined;
  }
};
export class PlayMetricsController {
  private readonly now: () => number;
  private readonly trackEvent: TrackEvent;
  private isDisposed = false;
  private lastPlaybackQuality?: {
    dropped: number;
    total: number;
  };
  private support: Required<PlayMetricsSupport> = {
    frameStats: false,
    longTask: false,
    slowInput: false
  };
  private window = createEmptyWindow();
  private windowStartedAt: number;
  constructor({
    now = () => Date.now(),
    trackEvent = trackFrontendEvent
  }: PlayMetricsControllerOptions = {}) {
    this.now = now;
    this.trackEvent = trackEvent;
    this.windowStartedAt = this.now();
  }
  setSupport(support: PlayMetricsSupport) {
    this.support = {
      ...this.support,
      ...support
    };
  }
  notePlaybackQuality(snapshot: PlaybackQualitySnapshot) {
    if (this.isDisposed || !this.support.frameStats) {
      return;
    }
    const dropped = toNonNegativeInt(snapshot.droppedVideoFrames);
    const total = toNonNegativeInt(snapshot.totalVideoFrames);
    if (dropped === undefined || total === undefined) {
      return;
    }
    if (!this.lastPlaybackQuality) {
      this.lastPlaybackQuality = {
        dropped,
        total
      };
      return;
    }
    this.window.droppedFrames += Math.max(dropped - this.lastPlaybackQuality.dropped, 0);
    this.window.totalFrames += Math.max(total - this.lastPlaybackQuality.total, 0);
    this.window.hasFrameStats = true;
    this.lastPlaybackQuality = {
      dropped,
      total
    };
  }
  noteLongTask(durationMs: number) {
    if (this.isDisposed || !this.support.longTask) {
      return;
    }
    const duration = toNonNegativeInt(durationMs);
    if (duration === undefined) {
      return;
    }
    this.window.longTaskCount += 1;
    this.window.longTaskBlockedMs += duration;
  }
  noteSlowInputDelay(delayMs: number) {
    if (this.isDisposed || !this.support.slowInput) {
      return;
    }
    const delay = toNonNegativeInt(delayMs);
    if (delay === undefined || delay < PLAY_SLOW_INPUT_THRESHOLD_MS) {
      return;
    }
    this.window.slowInputCount += 1;
    this.window.slowInputTotalDelayMs += delay;
    this.window.slowInputMaxDelayMs = Math.max(this.window.slowInputMaxDelayMs, delay);
  }
  flush() {
    if (this.isDisposed) {
      return;
    }
    const now = this.now();
    this.trackEvent(PLAY_METRICS_EVENT, {
      play_dropped_frames: this.support.frameStats && this.window.hasFrameStats ? this.window.droppedFrames : undefined,
      play_total_frames: this.support.frameStats && this.window.hasFrameStats ? this.window.totalFrames : undefined,
      play_frame_stats_support: this.support.frameStats ? 'supported' : 'unsupported',
      play_long_task_blocked_ms: this.support.longTask ? this.window.longTaskBlockedMs : undefined,
      play_long_task_count: this.support.longTask ? this.window.longTaskCount : undefined,
      play_long_task_support: this.support.longTask ? 'supported' : 'unsupported',
      play_slow_input_count: this.support.slowInput ? this.window.slowInputCount : undefined,
      play_slow_input_max_delay_ms: this.support.slowInput ? this.window.slowInputMaxDelayMs : undefined,
      play_slow_input_total_delay_ms: this.support.slowInput ? this.window.slowInputTotalDelayMs : undefined,
      play_slow_input_support: this.support.slowInput ? 'supported' : 'unsupported',
      play_memory_used_bytes: readMemoryUsedBytes(),
      play_window_duration_ms: toNonNegativeInt(now - this.windowStartedAt)
    }, 'frontend');
    this.windowStartedAt = now;
    this.window = createEmptyWindow();
  }
  dispose() {
    if (this.isDisposed) {
      return;
    }
    this.flush();
    this.isDisposed = true;
  }
}
function observeLongTasks(controller: PlayMetricsController): (() => void) | null {
  if (typeof PerformanceObserver === 'undefined') return null;
  try {
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        controller.noteLongTask(entry.duration);
      }
    });
    observer.observe({
      type: 'longtask',
      buffered: true
    });
    controller.setSupport({
      longTask: true
    });
    return () => observer.disconnect();
  } catch {
    controller.setSupport({
      longTask: false
    });
    return null;
  }
}
function observeSlowInput(controller: PlayMetricsController): (() => void) | null {
  if (typeof PerformanceObserver === 'undefined') return null;
  try {
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries() as Array<PerformanceEntry & {
        processingStart?: number;
      }>) {
        const delay = typeof entry.processingStart === 'number' ? entry.processingStart - entry.startTime : undefined;
        if (delay !== undefined) {
          controller.noteSlowInputDelay(delay);
        }
      }
    });
    observer.observe({
      type: 'event',
      buffered: true,
      durationThreshold: 16
    } as PerformanceObserverInit & {
      durationThreshold: number;
    });
    controller.setSupport({
      slowInput: true
    });
    return () => observer.disconnect();
  } catch {
    controller.setSupport({
      slowInput: false
    });
    return null;
  }
}
function startFlushInterval(controller: PlayMetricsController, getVideoElementRef: {
  current: (() => HTMLVideoElement | null | undefined) | undefined;
}): () => void {
  const supportsPlaybackQuality = typeof HTMLVideoElement !== 'undefined' && typeof HTMLVideoElement.prototype.getVideoPlaybackQuality === 'function';
  controller.setSupport({
    frameStats: supportsPlaybackQuality
  });
  const sampleAndFlush = () => {
    if (supportsPlaybackQuality) {
      const el = getVideoElementRef.current?.();
      if (el && typeof el.getVideoPlaybackQuality === 'function') {
        controller.notePlaybackQuality(el.getVideoPlaybackQuality());
      }
    }
    controller.flush();
  };
  const id = window.setInterval(sampleAndFlush, PLAY_METRICS_INTERVAL_MS);
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') sampleAndFlush();
  };
  window.addEventListener('pagehide', sampleAndFlush);
  document.addEventListener('visibilitychange', onVisibilityChange);
  return () => {
    clearInterval(id);
    window.removeEventListener('pagehide', sampleAndFlush);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
type UsePlayMetricsOptions = {
  getVideoElement?: () => HTMLVideoElement | null | undefined;
  disabled?: boolean;
};
export function usePlayMetrics({
  getVideoElement,
  disabled = false
}: UsePlayMetricsOptions = {}) {
  const getVideoElementRef = useRef(getVideoElement);
  getVideoElementRef.current = getVideoElement;
  useEffect(() => {
    if (disabled) return;
    const controller = new PlayMetricsController();
    const stopLongTasks = observeLongTasks(controller);
    const stopSlowInput = observeSlowInput(controller);
    const stopFlush = startFlushInterval(controller, getVideoElementRef);
    return () => {
      stopLongTasks?.();
      stopSlowInput?.();
      stopFlush();
      controller.dispose();
    };
  }, [disabled]);
}